import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Custom drag system for spawning character tokens onto the canvas.
 *
 * Deliberately bypasses HTML5 Drag-and-Drop. The HTML5 API is designed
 * for cross-application transfers (file uploads, dragging between
 * tabs), so the browser auto-populates the dataTransfer with whatever
 * MIMEs it thinks the OS/destination might want — text/uri-list from
 * an inner <img> src, text/html of the source's outerHTML, etc. If the
 * drop misses an accepting target, the browser's fallback for those
 * MIMEs can navigate the page. That was the source of the "drop
 * reloads the page" bug.
 *
 * Pointer events have no such machinery. mousedown → mousemove →
 * mouseup, with us tracking state and rendering a portal'd ghost
 * ourselves. The browser never sees a "drag" at all, so there's no
 * MIME, no navigation fallback, and full control of the preview.
 *
 * Drop targets (VTTCanvas) register a hit handler via
 * `registerDropTarget`. On pointerup, registered handlers are tried in
 * order; the first one that returns `true` consumes the drop. Targets
 * are responsible for their own hit-testing against the pointer coords
 * (e.g. via `getBoundingClientRect()`).
 */

export interface CharacterDragPayload {
  characterId: string
  name: string
  imageUrl?: string | null
  /**
   * The player who owns the dragged character, carried onto the spawned
   * token so canMoveToken / token disclosure treat it as that player's own.
   * Without it a Players-list drag lands the token owner-less — only the GM
   * could move it, and the owning player saw it follow fog/LOS instead of
   * being unconditionally shown (#164). The NPC-list "Place" button already
   * passes this; the drag path silently dropped it.
   */
  ownerUserId?: string
}

type DropHandler = (
  clientX: number,
  clientY: number,
  payload: CharacterDragPayload,
) => boolean

interface DragState {
  payload: CharacterDragPayload
  x: number
  y: number
}

interface CharacterDragContextValue {
  drag: DragState | null
  startDrag: (payload: CharacterDragPayload, e: ReactPointerEvent) => void
  registerDropTarget: (handler: DropHandler) => () => void
}

const CharacterDragContext = createContext<CharacterDragContextValue | null>(
  null,
)

export function CharacterDragProvider({ children }: { children: ReactNode }) {
  const [drag, setDrag] = useState<DragState | null>(null)
  const handlersRef = useRef<Set<DropHandler>>(new Set())
  const dragRef = useRef<DragState | null>(null)
  dragRef.current = drag

  const startDrag = useCallback(
    (payload: CharacterDragPayload, e: ReactPointerEvent) => {
      // Block text selection / focus shifts during the drag.
      e.preventDefault()
      console.log('[CharacterDrag] startDrag', {
        characterId: payload.characterId,
        name: payload.name,
        x: e.clientX,
        y: e.clientY,
      })
      setDrag({ payload, x: e.clientX, y: e.clientY })
    },
    [],
  )

  const registerDropTarget = useCallback((handler: DropHandler) => {
    handlersRef.current.add(handler)
    return () => {
      handlersRef.current.delete(handler)
    }
  }, [])

  // Attach window listeners only while a drag is in flight. Coordinates
  // come straight off the events, and the position state is updated via
  // functional setState, so the listeners don't need to be reattached on
  // every move — the boolean `drag !== null` is the only trigger that
  // matters.
  const isDragging = drag !== null
  useEffect(() => {
    if (!isDragging) return

    const onMove = (e: PointerEvent) => {
      setDrag((prev) =>
        prev ? { ...prev, x: e.clientX, y: e.clientY } : null,
      )
    }
    const onUp = (e: PointerEvent) => {
      const current = dragRef.current
      console.log('[CharacterDrag] pointerup', {
        x: e.clientX,
        y: e.clientY,
        targetingHandlers: handlersRef.current.size,
        hasDrag: !!current,
      })
      if (current) {
        let accepted = false
        for (const handler of handlersRef.current) {
          if (handler(e.clientX, e.clientY, current.payload)) {
            accepted = true
            break
          }
        }
        console.log('[CharacterDrag] drop result', { accepted })
      }
      setDrag(null)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrag(null)
    }

    console.log('[CharacterDrag] attaching window listeners')
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('keydown', onEsc)

    return () => {
      console.log('[CharacterDrag] detaching window listeners')
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('keydown', onEsc)
    }
  }, [isDragging])

  return (
    <CharacterDragContext.Provider
      value={{ drag, startDrag, registerDropTarget }}
    >
      {children}
    </CharacterDragContext.Provider>
  )
}

export function useCharacterDrag(): CharacterDragContextValue {
  const ctx = useContext(CharacterDragContext)
  if (!ctx) {
    throw new Error(
      'useCharacterDrag must be used within a CharacterDragProvider',
    )
  }
  return ctx
}

/**
 * Floating preview rendered while a character drag is in flight.
 * Portaled to body, fixed-positioned, pointer-events-none so it never
 * intercepts the hit testing the drop targets do.
 */
export function CharacterDragGhost() {
  const { drag } = useCharacterDrag()
  if (!drag) return null

  const portrait = drag.payload.imageUrl
  const initial = (drag.payload.name || '?').charAt(0).toUpperCase()

  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: drag.x,
        top: drag.y,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.375rem 0.625rem 0.375rem 0.375rem',
        background: 'hsl(var(--ac-dark-secondary) / 0.95)',
        border: '1px solid hsl(var(--ac-mint-primary) / 0.5)',
        borderRadius: 'var(--ac-radius-full)',
        boxShadow: '0 4px 16px hsl(0 0% 0% / 0.5)',
        fontSize: '0.8125rem',
        color: 'hsl(var(--ac-text-light))',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {portrait ? (
        <div
          role="img"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundImage: `url(${JSON.stringify(portrait)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'hsl(var(--ac-dark-primary))',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--ac-font-serif)',
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      <span style={{ fontWeight: 500 }}>{drag.payload.name}</span>
    </div>,
    document.body,
  )
}
